import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouteContext, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { DIRECTION_MAX_CHARS, EDIT_PACES, FOOTAGE_KINDS, HOUSE_BRAND_ID, LONG_CLIP_MS, PRIORITY_LINES_MAX, PRIORITY_LINE_MAX_CHARS, type BrandSettings, type InviteRole } from '@studio/contracts';
import { AutoMark } from '@/components/auto-marker';
import { Masonry } from '@/components/masonry';
import { PillList } from '@/components/pill-editor';
import { SaveStatus } from '@/components/save-status';
import { TopBar } from '@/components/shell';
import { TaggingCategories } from '@/components/tagging-categories';
import { ToolField } from '@/components/toolbar';
import { useAutosave, type SaveState } from '@/hooks/use-autosave';
import { queryClient, trpc } from '@/lib/trpc';
import type { trpcClient } from '@/lib/trpc';

const TIMEZONES = ['Africa/Johannesburg', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'Asia/Dubai', 'Australia/Sydney'];

/**
 * Owner-only brand settings: a masonry of cards (website auto-fill, visual direction, the composer's
 * edit pace, typical footage and cuts, the brand's approval and posting settings, the team, usage and its
 * reset) with the tagging categories beneath. Every heading carries its explanation as a hover tip.
 */
export function SettingsPage() {
  const search = useSearch({ from: '/_app/settings' });
  const { me } = useRouteContext({ from: '/_app' });
  const current = useQuery(trpc.tenant.settings.get.queryOptions());
  const [form, setForm] = useState<BrandSettings | null>(null);
  const [touched, setTouched] = useState<Set<keyof BrandSettings>>(new Set());
  useEffect(() => {
    if (current.data && !form) setForm(current.data);
  }, [current.data, form]);
  // Autosaved; a response never overwrites the form, so typing during a save is not lost.
  const save = useMutation(trpc.tenant.settings.set.mutationOptions({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trpc.tenant.settings.get.queryKey() });
      void queryClient.invalidateQueries({ queryKey: trpc.tenant.me.queryKey() });
    },
  }));
  const autosave = useAutosave((next: BrandSettings) => save.mutateAsync(next));
  const set = <K extends keyof BrandSettings>(key: K, value: BrandSettings[K]): void => {
    if (!form) return;
    const next = { ...form, [key]: value };
    setForm(next);
    setTouched((t) => new Set(t).add(key));
    autosave.schedule(next);
  };
  const src = (key: keyof BrandSettings) => (touched.has(key) ? 'user' : 'auto');
  const defaults = useAnalyseDefaults();
  // One indicator for the page's two autosaves: a failure shows first, then work in progress.
  const states = [autosave.state, defaults.state];
  const saveState: SaveState = states.includes('error') ? 'error' : states.includes('saving') ? 'saving' : states.includes('saved') ? 'saved' : 'idle';

  return (
    <>
      <TopBar title="Brand settings" right={<SaveStatus state={saveState} error={autosave.error ?? defaults.error} />} />
      <section className="content col">
        {current.isError && <div className="card pad error" role="alert">{current.error.message}</div>}
        {save.isError && <div className="card pad error" role="alert">{save.error.message}</div>}
        <div className="settings-page">
          <Masonry className="settings-masonry" items={[
            { id: 'site', node: <WebsiteAutoFill /> },
            { id: 'direction', node: <DirectionCard d={defaults} /> },
            { id: 'pace', node: <PaceCard d={defaults} /> },
            { id: 'footage', node: <FootageCard d={defaults} /> },
            { id: 'brand', node: form && <BrandCard form={form} set={set} src={src} /> },
            { id: 'team', node: <TeamPanel /> },
            { id: 'usage', node: <UsageCard /> },
          ]} />
          <TaggingCategories focus={search.category ?? null} />
          {me?.active?.brandId === HOUSE_BRAND_ID && <TaggingCategories media="audio" />}
        </div>
      </section>
    </>
  );
}

/** Approval, sharing, posting rhythm and timezone; autosaves. */
function BrandCard({ form, set, src }: {
  form: BrandSettings;
  set: <K extends keyof BrandSettings>(key: K, value: BrandSettings[K]) => void;
  src: (key: keyof BrandSettings) => 'user' | 'auto';
}) {
  return (
    <div className="card pad settings-card">
      <div className="h2" title="Approval, sharing, posting rhythm and timezone for this brand. Autosaves.">Schedule and approvals</div>
      <div className="form">
        <div className="k">Approval mode<AutoMark source={src('approvalMode')} /><small>Client review sends every post to a reviewer; self-approve skips it when a channel is connected.</small></div>
        <div className="v"><div className="seg" role="radiogroup" aria-label="Approval mode">
          <button type="button" role="radio" aria-checked={form.approvalMode === 'client'} className={form.approvalMode === 'client' ? 'on' : ''} onClick={() => set('approvalMode', 'client')}>Client review</button>
          <button type="button" role="radio" aria-checked={form.approvalMode === 'self'} className={form.approvalMode === 'self' ? 'on' : ''} onClick={() => set('approvalMode', 'self')}>Self-approve</button>
        </div></div>

        <div className="k">Share within organisation<AutoMark source={src('shareWithinOrg')} /><small>Sibling brands in the same organisation see and may use this brand's assets.</small></div>
        <div className="v"><label className="row"><input type="checkbox" checked={form.shareWithinOrg} onChange={(e) => set('shareWithinOrg', e.target.checked)} /> <span>{form.shareWithinOrg ? 'On' : 'Off'}</span></label></div>

        <div className="k">Daily shape<AutoMark source={src('dailyShape')} /><small>Posts booked per day: trending audio and original sound.</small></div>
        <div className="v">
          <label className="field"><input type="number" min={0} max={10} value={form.dailyShape.trending} onChange={(e) => set('dailyShape', { ...form.dailyShape, trending: Number(e.target.value) })} aria-label="Trending per day" /><span className="sub">trending</span></label>
          <label className="field"><input type="number" min={0} max={10} value={form.dailyShape.original} onChange={(e) => set('dailyShape', { ...form.dailyShape, original: Number(e.target.value) })} aria-label="Original per day" /><span className="sub">original</span></label>
        </div>

        <div className="k">Cooldown<AutoMark source={src('cooldownDays')} /><small>A snippet rests for both thresholds before it can be used again.</small></div>
        <div className="v">
          <label className="field"><input type="number" min={0} value={form.cooldownDays} onChange={(e) => set('cooldownDays', Number(e.target.value))} aria-label="Cooldown days" /><span className="sub">days</span></label>
          <label className="field"><input type="number" min={0} value={form.cooldownPosts} onChange={(e) => set('cooldownPosts', Number(e.target.value))} aria-label="Cooldown posts" /><span className="sub">posts</span></label>
        </div>

        <div className="k">Timezone<AutoMark source={src('timezone')} /><small>Slots are booked in the brand's day.</small></div>
        <div className="v"><label className="field"><select value={form.timezone} onChange={(e) => set('timezone', e.target.value)} aria-label="Timezone">{[...new Set([form.timezone, ...TIMEZONES])].map((tz) => <option key={tz} value={tz}>{tz}</option>)}</select></label></div>
      </div>
    </div>
  );
}

const ROLE_OPTIONS: { value: InviteRole; label: string }[] = [
  { value: 'editor', label: 'Editor' },
  { value: 'owner', label: 'Owner' },
  { value: 'reviewer', label: 'Reviewer' },
  { value: 'creator', label: 'Creator' },
];

/** The organisation's team: who has which role where, and the invites still out. */
function TeamPanel() {
  const team = useQuery(trpc.team.members.queryOptions());
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('editor');
  const [name, setName] = useState('');
  const [thisBrandOnly, setThisBrandOnly] = useState(false);
  const creator = role === 'creator';
  const refetch = () => void queryClient.invalidateQueries({ queryKey: trpc.team.members.queryKey() });
  const invite = useMutation(trpc.team.invite.mutationOptions({ onSuccess: () => { setEmail(''); setName(''); refetch(); } }));
  const remove = useMutation(trpc.team.remove.mutationOptions({ onSuccess: refetch }));
  return (
    <div className="card pad team-panel">
      <div className="h2" title="Owners run the brand and its team; editors produce; reviewers approve; creators only upload.">Team</div>
      <div className="row team-invite">
        <label className="field team-email"><input type="email" value={email} placeholder="teammate@company.com" aria-label="Invite email"
          onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="field team-role">
          <select value={role} aria-label="Invite role" onChange={(e) => setRole(e.target.value as InviteRole)}>
            {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </label>
        {creator ? (
          <label className="field team-name"><input type="text" value={name} placeholder="Creator name" aria-label="Creator name"
            onChange={(e) => setName(e.target.value)} /></label>
        ) : (
          <label className="row team-scope"><input type="checkbox" checked={thisBrandOnly} onChange={(e) => setThisBrandOnly(e.target.checked)} /> <span className="sub">This brand only</span></label>
        )}
        <button type="button" className="btn primary" disabled={invite.isPending || !email.includes('@')}
          onClick={() => invite.mutate({ email: email.trim().toLowerCase(), role, thisBrandOnly, ...(creator && name.trim() ? { name: name.trim() } : {}) })}>
          {invite.isPending ? 'Inviting…' : 'Invite'}
        </button>
      </div>
      {invite.isSuccess && (
        <div className="row">
          <span className="chip ok">{invite.data.pending ? 'Invite sent' : 'Added'}</span>
          {!invite.data.sent && <span className="sub">No email went out; share this link: <a href={invite.data.link}>{invite.data.link}</a></span>}
        </div>
      )}
      {invite.isError && <div className="error" role="alert">{invite.error.message}</div>}
      {remove.isError && <div className="error" role="alert">{remove.error.message}</div>}
      {team.isError && <div className="error" role="alert">{team.error.message}</div>}
      {team.data && (
        <table className="tbl">
          <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Scope</th><th>Status</th><th /></tr></thead>
          <tbody>
            {team.data.members.map((m) => (
              <tr key={`${m.accountId ?? m.inviteId}:${m.brand ?? 'org'}`}>
                <td><b>{m.email}</b></td>
                <td className="sub">{m.name}</td>
                <td>{m.role}</td>
                <td className="sub">{m.brand ?? 'Whole organisation'}</td>
                <td>{m.pending ? <span className="chip warn">invited</span> : <span className="chip ok">active</span>}</td>
                <td>
                  {m.accountId !== team.data.me && (
                    <button type="button" className="btn quiet team-remove" disabled={remove.isPending}
                      onClick={() => remove.mutate(m.inviteId ? { inviteId: m.inviteId } : { accountId: m.accountId! })}>Remove</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {team.isPending && <div className="spinner" aria-label="Loading team" />}
    </div>
  );
}

type DefaultsForm = Awaited<ReturnType<typeof trpcClient.brandSettings.analyseDefaults.query>>['explicit'];

const DIRECTION_PLACEHOLDER = 'In your own words: what your footage is about and how you want it cut';
const PRIMARY_PLACEHOLDER = 'add a highlight, e.g. the tasting with the reaction, as one clip';
const SECONDARY_PLACEHOLDER = 'add a supporting shot, e.g. the making-of steps';

/**
 * The analyser's brand defaults: one form behind three cards (the composer's edit pace, the visual
 * direction, how the footage usually runs plus the cut numbers). Everything autosaves on a short
 * debounce; there is no Save button.
 */
interface DefaultsState {
  form: DefaultsForm | null;
  commit: (next: DefaultsForm) => void;
  avg: Partial<Record<'minSnippets' | 'maxSnippets' | 'minSnippetMs' | 'maxSnippetMs' | 'sceneThreshold', number>> | undefined;
  state: SaveState;
  error: string | null;
}
const SCOPE_TIP = 'Autosaves. Applies to this brand; each sub-brand keeps its own.';

function useAnalyseDefaults(): DefaultsState {
  const q = useQuery(trpc.brandSettings.analyseDefaults.queryOptions());
  const [form, setForm] = useState<DefaultsForm | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm(q.data.explicit);
  }, [q.data, form]);
  const save = useMutation(trpc.brandSettings.setAnalyseDefaults.mutationOptions({
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: trpc.brandSettings.analyseDefaults.queryKey() }),
  }));
  const autosave = useAutosave((next: DefaultsForm) => save.mutateAsync(next));
  const commit = (next: DefaultsForm) => {
    setForm(next);
    autosave.schedule(next);
  };
  return { form, commit, avg: q.data?.average, state: autosave.state, error: autosave.error };
}

function PaceCard({ d }: { d: DefaultsState }) {
  const { form, commit } = d;
  if (!form) return null;
  return (
      <div className="card pad settings-card">
        <div className="settings-head">
          <div className="h2" title={`How long each clip runs when the composer assembles an edit for you. ${SCOPE_TIP}`}>Composer edit pace</div>
        </div>
        <span className="sub settings-note">When the Composer builds an edit for you, every clip is trimmed to fit this pace, so it sets how quick the finished video feels. Clips you trim by hand keep your length.</span>
        <table className="tbl pace-tbl">
          <tbody>
            {[{ key: null as DefaultsForm['paceKey'], label: 'Variable · clip-by-clip', hint: 'No preference override: let the brief decide each time' },
              ...EDIT_PACES.map((p) => ({ key: p.key as DefaultsForm['paceKey'], label: p.label, hint: p.hint })),
              { key: 'custom' as const, label: 'Custom', hint: '' }].map((row) => (
              <tr key={row.key ?? 'none'} className={form.paceKey === row.key ? 'lib-picked' : ''}>
                <td className="check-cell">
                  <input type="radio" name="pace" checked={form.paceKey === row.key} aria-label={row.label}
                    onChange={() => commit({ ...form, paceKey: row.key, paceRecommended: null })} />
                </td>
                <td>
                  <b>{row.label}</b>
                  {form.paceRecommended && form.paceKey === row.key && row.key !== null && <span className="chip ok pace-rec">recommended</span>}
                  {row.key === 'custom' ? (
                    <span className="row pace-custom">
                      <input type="number" min={0.2} max={20} step={0.1} value={form.paceMinMs !== null ? form.paceMinMs / 1000 : ''} placeholder="min s" aria-label="Custom pace minimum seconds"
                        onChange={(e) => commit({ ...form, paceKey: 'custom', paceRecommended: null, paceMinMs: e.target.value === '' ? null : Math.round(Number(e.target.value) * 1000) })} />
                      <span>–</span>
                      <input type="number" min={0.3} max={30} step={0.1} value={form.paceMaxMs !== null ? form.paceMaxMs / 1000 : ''} placeholder="max s" aria-label="Custom pace maximum seconds"
                        onChange={(e) => commit({ ...form, paceKey: 'custom', paceRecommended: null, paceMaxMs: e.target.value === '' ? null : Math.round(Number(e.target.value) * 1000) })} />
                      <span>s per clip</span>
                    </span>
                  ) : (
                    <span className="sub pace-hint">{row.hint}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
  );
}

function DirectionCard({ d }: { d: DefaultsState }) {
  const { form, commit } = d;
  if (!form) return null;
  return (
      <div className="card pad settings-card">
        <div className="settings-head">
          <div className="h2" title={`The analyser reads this before it picks a clip's highlights and joins its scenes. ${SCOPE_TIP}`}>Visual direction</div>
        </div>
        <span className="sub settings-note">What are the priorities in your footage, and how do you want the AI to extract?</span>
        <label className="field-stack" title="Anything you want the analyser to know, in your own words; it goes to the AI as written.">
          <span className="label">Prompt</span>
          <textarea className="field lines-field" rows={3} value={form.direction} placeholder={DIRECTION_PLACEHOLDER} maxLength={DIRECTION_MAX_CHARS} aria-label="Direction to the analyser"
            onChange={(e) => commit({ ...form, direction: e.target.value })} />
        </label>
        <div className="field-stack" title="Moments the analyser always cuts out as a highlight of their own, with their natural start and end. Most important first.">
          <span className="label">Highlights</span>
          <PillList items={form.primaryShots} placeholder={PRIMARY_PLACEHOLDER} label="Add a highlight" maxLength={PRIORITY_LINE_MAX_CHARS} max={PRIORITY_LINES_MAX}
            onChange={(primaryShots) => commit({ ...form, primaryShots })} />
        </div>
        <div className="field-stack" title="Shots worth finding and searching for that never lead ahead of the highlights. Consecutive steps that look alike fold into one scene.">
          <span className="label">Supporting shots</span>
          <PillList items={form.secondaryShots} placeholder={SECONDARY_PLACEHOLDER} label="Add a supporting shot" maxLength={PRIORITY_LINE_MAX_CHARS} max={PRIORITY_LINES_MAX}
            onChange={(secondaryShots) => commit({ ...form, secondaryShots })} />
        </div>
      </div>
  );
}

function FootageCard({ d }: { d: DefaultsState }) {
  const { form, commit, avg, error } = d;
  if (!form) return null;
  return (
      <div className="card pad settings-card">
        <div className="settings-head">
          <div className="h2" title={`How your clips usually run tells the analyser whether to cut by eye or by ear. ${SCOPE_TIP}`}>Typical footage</div>
        </div>
        <table className="tbl pace-tbl">
          <tbody>
            {[...FOOTAGE_KINDS.map((k) => ({ key: k.key as DefaultsForm['footageKey'], label: k.label, hint: k.hint })), { key: 'custom' as const, label: 'Custom', hint: '' }].map((row) => (
              <tr key={row.key ?? 'none'} className={form.footageKey === row.key ? 'lib-picked' : ''}>
                <td className="check-cell">
                  <input type="radio" name="footage" checked={form.footageKey === row.key} aria-label={row.label}
                    onChange={() => commit({ ...form, footageKey: row.key })} />
                </td>
                <td>
                  <b>{row.label}</b>
                  {row.key === 'custom' ? (
                    <span className="row pace-custom">
                      <input type="number" min={1} max={7200} step={1} value={form.footageMinS ?? ''} placeholder="min s" aria-label="Typical length minimum seconds"
                        onChange={(e) => commit({ ...form, footageKey: 'custom', footageMinS: e.target.value === '' ? null : Math.round(Number(e.target.value)) })} />
                      <span>–</span>
                      <input type="number" min={2} max={7200} step={1} value={form.footageMaxS ?? ''} placeholder="max s" aria-label="Typical length maximum seconds"
                        onChange={(e) => commit({ ...form, footageKey: 'custom', footageMaxS: e.target.value === '' ? null : Math.round(Number(e.target.value)) })} />
                      <span>s per clip</span>
                    </span>
                  ) : (
                    <span className="sub pace-hint">{row.hint}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <label className="row" title={`Clips over ${LONG_CLIP_MS / 60_000} minutes. Off: transcribe one from its menu when you need it.`}>
          <input type="checkbox" checked={form.transcribeLong} onChange={(e) => commit({ ...form, transcribeLong: e.target.checked })} />
          <span>Transcribe speech in long clips</span>
        </label>
        <div className="label" title="How many highlights a clip yields and how long they run. Blank fields stay elastic: the clip's own variation and length decide, drifting to what you usually set.">Cuts</div>
        <div className="row defaults-steppers">
          <ToolField label="Min clips" value={form.minSnippets} placeholder={avg?.minSnippets ? String(avg.minSnippets) : 'auto'} min={1} max={30} base={avg?.minSnippets ?? 3} width="narrow"
            onChange={(v) => commit({ ...form, minSnippets: v })} />
          <ToolField label="Max clips" value={form.maxSnippets} placeholder={avg?.maxSnippets ? String(avg.maxSnippets) : 'auto'} min={1} max={30} base={avg?.maxSnippets ?? 6} width="narrow"
            onChange={(v) => commit({ ...form, maxSnippets: v })} />
          <ToolField label="Min s" value={form.minSnippetMs !== null ? form.minSnippetMs / 1000 : null} placeholder={avg?.minSnippetMs ? (avg.minSnippetMs / 1000).toFixed(1) : '0.7'} min={0.1} max={30} step={0.1} base={avg?.minSnippetMs ? avg.minSnippetMs / 1000 : 0.7} width="narrow"
            onChange={(v) => commit({ ...form, minSnippetMs: v === null ? null : Math.round(v * 1000) })} />
          <ToolField label="Max s" value={form.maxSnippetMs !== null ? form.maxSnippetMs / 1000 : null} placeholder={avg?.maxSnippetMs ? (avg.maxSnippetMs / 1000).toFixed(1) : 'auto'} min={0.2} max={60} step={0.1} base={avg?.maxSnippetMs ? avg.maxSnippetMs / 1000 : 5} width="narrow"
            onChange={(v) => commit({ ...form, maxSnippetMs: v === null ? null : Math.round(v * 1000) })} />
          <ToolField label="Scene sens." value={form.sceneThreshold} placeholder={avg?.sceneThreshold ? String(avg.sceneThreshold) : '0.3'} min={0.1} max={0.9} step={0.05} base={avg?.sceneThreshold ?? 0.3} width="narrow"
            onChange={(v) => commit({ ...form, sceneThreshold: v })} />
        </div>
        {error && <div className="error" role="alert">{error}</div>}
      </div>
  );
}

/** The auto button: point at the brand's website and the vocabulary fills itself. */
function WebsiteAutoFill() {
  const [url, setUrl] = useState('');
  const scrape = useMutation(trpc.facets.scrapeWebsite.mutationOptions({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trpc.facets.list.queryKey() });
      void queryClient.invalidateQueries({ queryKey: trpc.brandSettings.analyseDefaults.queryKey() });
    },
  }));
  const submit = () => {
    const raw = url.trim();
    if (!raw) return;
    scrape.mutate({ url: raw.startsWith('http') ? raw : `https://${raw}` });
  };
  return (
    <div className="card pad scrape-panel settings-card">
      <div className="settings-head">
        <div className="h2" title="Reads your website once and loads products, flavours and services into the tagging categories, and suggests an edit pace.">Auto-fill from your website</div>
        <span className="sub">Reads your site and loads products, flavours and services into the tagging categories below</span>
      </div>
      <div className="row">
        <label className="field scrape-url"><input value={url} placeholder="www.yourbrand.com" aria-label="Website address"
          onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} /></label>
        <button type="button" className="btn primary" disabled={scrape.isPending || !url.trim()} onClick={submit}>
          {scrape.isPending ? 'Reading the site…' : 'Auto-fill'}
        </button>
      </div>
      {scrape.isError && <div className="error" role="alert">{scrape.error.message}</div>}
      {scrape.data && (
        <div className="row scrape-results">
          <span className="chip ok">{scrape.data.added.length} added from {scrape.data.scanned}</span>
          {scrape.data.recommendedPace && <span className="chip acc">Edit pace recommended: {EDIT_PACES.find((p) => p.key === scrape.data.recommendedPace)?.label ?? scrape.data.recommendedPace}</span>}
          {scrape.data.alreadyKnown > 0 && <span className="chip">{scrape.data.alreadyKnown} already known</span>}
          {scrape.data.added.map((v) => <span key={`${v.key}:${v.label}`} className="chip acc">{v.key} · {v.label}</span>)}
        </div>
      )}
    </div>
  );
}

const RESET_CHOICES = [
  { scope: 'published' as const, label: 'Published usage', hint: 'Approved and published posts stop counting towards how often a clip or sound was used. The posts stay.' },
  { scope: 'drafts' as const, label: 'Draft history', hint: 'The Composer forgets which footage your drafts placed, so it offers it again. The drafts stay.' },
  { scope: 'all' as const, label: 'Everything', hint: 'Both of the above.' },
];

/**
 * Usage (2026-10-04): a clip or sound counts as used when a post that plays it is approved. The reset
 * clears that, the Composer's memory of what the drafts placed, or both; posts and drafts are kept.
 */
function UsageCard() {
  const summary = useQuery(trpc.brandSettings.usageSummary.queryOptions());
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<'published' | 'drafts' | 'all'>('all');
  const reset = useMutation(trpc.brandSettings.resetUsage.mutationOptions({
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: trpc.brandSettings.usageSummary.queryKey() });
      void queryClient.invalidateQueries({ queryKey: trpc.library.list.queryKey() });
    },
  }));
  const s = summary.data;
  return (
    <div className="card pad settings-card">
      <div className="settings-head">
        <div className="h2" title="A clip or sound counts as used when a post that plays it is approved. Composing and discarding drafts never counts.">Usage</div>
      </div>
      {s && <span className="sub">{s.approvedPosts} approved {s.approvedPosts === 1 ? 'post' : 'posts'} · {s.publishedPosts} published · {s.draftStamps} draft {s.draftStamps === 1 ? 'record' : 'records'}</span>}
      <button type="button" className="btn danger lg full" onClick={() => setOpen(true)}>Reset usage…</button>
      {open && (
        <>
          <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setOpen(false)} />
          <div className="modal reset-modal" role="dialog" aria-label="Reset usage">
            <h2>Reset usage</h2>
            {RESET_CHOICES.map((c) => (
              <label key={c.scope} className="row reset-row">
                <input type="radio" name="usage-reset" checked={scope === c.scope} onChange={() => setScope(c.scope)} />
                <span className="col reset-text"><b>{c.label}</b><span className="sub">{c.hint}</span></span>
              </label>
            ))}
            {reset.isError && <div className="error" role="alert">{reset.error.message}</div>}
            <div className="row between">
              <button type="button" className="btn quiet" onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className="btn danger" disabled={reset.isPending} onClick={() => reset.mutate({ scope })}>{reset.isPending ? 'Resetting…' : 'Reset'}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
